import Lara from "@/presets/lara";

const stepDefaultPt = Lara.steps;
const buttonDefaultPt = Lara.button;
const multiselectDefaultPt = Lara.multiselect;
const dataTableDefaultPt = Lara.datatable;
const dialogDefaultPt = Lara.dialog;
const dropdownDefaultPt = Lara.dropdown;
const progressSpinnerDefaultPt = Lara.progressspinner;

const checkboxIndigoPt = {
  box: ({ context, props }) => ({
    class: [
      ...Lara.checkbox.box({ context, props }).class,
      {
        "!bg-indigo-base !border-indigo-base": context.checked,
      },
      "!border-indigo-base rounded-md",
    ],
  }),
  icon: {
    class: [...Lara.checkbox.icon.class],
  },
};

export const stepsPt = (activeStep: number) => ({
  ...stepDefaultPt,
  label: ({ context }) => {
    return {
      class: [
        ...stepDefaultPt.label({ context }).class.filter((c) => {
          if (typeof c === "object" && c !== null && "font-bold" in c) return false;
          return true;
        }),
        "pl-2 pb-1 mr-5",
        context.active ? "!text-indigo-base" : "!text-blueGray-500",
      ],
    };
  },
  action: (options) => {
    return {
      class: [...stepDefaultPt.action(options).class.filter((c) => c !== "flex-col"), "flex align-items-center z-20"],
    };
  },
  step: ({ context, props }) => {
    return {
      class: [
        ...stepDefaultPt.step({ context, props }).class,
        {
          "!bg-indigo-base dark:bg-primary-400": context.active,
          "!border-indigo-base dark:border-primary-400": context.active,
          "!text-surface-0 dark:text-surface-900": context.active,
          "!border-2 !border-blueGray-200 !text-blueGray-500": !context.active,
        },
        "ml-5",
      ],
    };
  },
  menuitem: (options) => {
    const index = options?.context?.index ?? 0;
    const isFirst = index === 0;
    const isSecondStep = index === 1;
    const fillFirstHalf = index === 0 && activeStep === 2;
    const fillSecondHalf = index === 1 && options?.context?.active;

    return {
      class: [
        ...stepDefaultPt.menuitem.class.filter((c) => {
          return (
            typeof c !== "string" ||
            !(c === "before:top-1/2" || c === "before:-mt-4" || c === "before:transform" || c === "before:w-full")
          );
        }),
        {
          "before:w-40": isSecondStep,
          "before:right-0 before:left-auto before:w-40": isFirst,
        },
        "before:top-4",
        "before:border-t-2",
        fillFirstHalf || fillSecondHalf ? ["before:!border-indigo-base"] : [],
      ],
    };
  },
  root: {
    class: ["px-40 pb-10"],
  },
});

export const cancelButtonPt = {
  root: ({ props, context, parent }) => {
    return {
      class: [
        ...buttonDefaultPt.root({ props, context, parent }).class.filter((c) => {
          return !(c && typeof c === "object" && Object.keys(c).some((k) => k.includes("text-white")));
        }),
        "border border-teal-900 bg-white rounded-md w-20 text-teal-900 hover:text-white text-md",
      ],
    };
  },
  label: {
    class: "duration-200",
  },
};

export const nextButtonPt = {
  root: ({ props, context, parent }) => {
    return {
      class: [
        ...buttonDefaultPt.root({ props, context, parent }).class,
        "border border-teal-900 bg-teal-900 rounded-md min-w-20 max-w-full text-md",
      ],
    };
  },
  label: {
    class: "duration-200",
  },
};

export const backButtonPt = {
  root: ({ props, context, parent }) => {
    return {
      class: [
        ...buttonDefaultPt.root({ props, context, parent }).class,
        "border !border-blueGray-100 !bg-blueGray-100 !text-blueGray-600 w-20 text-md",
      ],
    };
  },
  label: {
    class: "duration-200",
  },
};

export const multiselectPt = {
  ...multiselectDefaultPt,
  root: ({ props, state }) => ({
    class: [
      "inline-flex relative rounded-md bg-surface-0 dark:bg-surface-900 border",
      { "border-indigo-base": !props.invalid },
      "transition-all duration-200 cursor-pointer select-none min-w-0 max-w-full w-full",
    ],
  }),
  labelContainer: {
    class: [multiselectDefaultPt.labelContainer.class, "flex-wrap w-full overflow-x-hidden min-w-0"],
  },
  label: ({ props }) => ({
    class: [multiselectDefaultPt.label({ props }).class, "!text-blueGray-500"],
  }),
  container: {
    class:
      "font-sans text-base border border-none rounded-sm leading-none flex items-center flex-wrap gap-2 m-0 list-none px-3 py-1.5 w-full min-h-[2.877rem] appearance-none text-surface-700 dark:text-white/80 placeholder:text-surface-400 dark:placeholder:text-surface-500 bg-surface-0 dark:bg-surface-900 border border-surface-300 dark:border-surface-600 hover:border-primary-500 dark:hover:border-primary-400 focus:outline-none focus:outline-offset-0 transition duration-200 ease-in-out cursor-text overflow-hidden",
  },
  header: {
    class: [multiselectDefaultPt.header.class, "!bg-white"],
  },
  token: {
    class:
      "inline-flex items-center py-1.5 px-3 mr-2 rounded-md cursor-default !bg-blueGray-100 text-indigo-base text-md",
  },
  item: ({ context }) => ({
    class: [
      ...multiselectDefaultPt.item({ context }).class,
      "!py-2 w-full",
      {
        "bg-white !text-indigo-base hover:bg-surface-100": context.focused && !context.selected,
      },
      {
        "bg-white !text-indigo-base hover:bg-surface-100": context.focused && context.selected,
      },
      {
        "bg-white !text-indigo-base hover:bg-surface-100": !context.focused && context.selected,
      },
    ],
  }),
  headerCheckboxContainer: {
    class: "!hidden",
  },
  headerCheckbox: {
    root: {
      class: "!hidden",
    },
  },
  itemCheckbox: checkboxIndigoPt,
  filterinput: {
    class: [
      "mt-2 font-sans leading-none pr-7 py-3 px-3 -mr-7 w-full h-10",
      "text-surface-700 bg-white",
      "border border-gray-150",
      "placeholder:text-indigo-base",
      "rounded-lg appearance-none transition duration-200 outline-none focus:outline-none focus:ring-0 focus:ring-offset-0",
    ],
  },
  filtericon: {
    class: ["absolute absolute top-1/2 right-3 -mt-1 text-gray-500"],
  },
  closeButton: {
    class: "!hidden",
    style: { display: "none !important" },
  },
  closeicon: {
    class: ["!hidden"],
  },
};

export const advisorDropdownPt = {
  ...dropdownDefaultPt,
  root: ({ props, state }) => ({
    class: [dropdownDefaultPt.root({ props, state }).class, "w-full !rounded-md !border-indigo-base"],
  }),
  panel: {
    class: [dropdownDefaultPt.panel.class, "!bg-white shadow-md"],
  },
  filterinput: {
    class: [dropdownDefaultPt.filterinput.class, "border-gray-400 focus:ring-0 focus:border-teal-900"],
  },
};

export const datatablePt = {
  ...dataTableDefaultPt,
  table: {
    ...dataTableDefaultPt.table,
    class: [dataTableDefaultPt.table?.class, "!table-auto w-full"],
  },
  bodyrow: {
    class: ["border-spacing-0 bg-surface-0 text-blueGray-800"],
  },
  column: {
    headercell: ({ context, props }) => ({
      class: [
        ...dataTableDefaultPt.column.headercell({ context, props }).class.filter((c) => c !== "font-bold"),
        "!text-indigo-base !font-medium !text-md",
      ],
    }),
  },
};

export const dialogPt = {
  root: ({ state }) => ({
    class: [...dialogDefaultPt.root({ state }).class, "!w-3/5"],
  }),
  header: {
    class: [...dialogDefaultPt.header.class, "!font-semibold rounded-t-xl !bg-teal-accent"],
  },
  footer: {
    class: [...dialogDefaultPt.footer.class, "rounded-b-xl"],
  },
  closeButton: {
    class: [
      "relative flex items-center justify-center mr-2 last:mr-0 w-4 h-4 border-0 rounded-full text-surface-500 text-university-secondary bg-transparent transition duration-200 ease-in-out",
    ],
  },
};

export const progressSpinnerPt = {
  root: {
    class: [progressSpinnerDefaultPt.root.class, "!w-16 !h-16"],
  },
  circle: {
    class: ["progress-spinner-circle"],
    style: {
      stroke: "#D5E9E7",
      animation: "p-progress-spinner-dash 1.5s ease-in-out infinite",
    },
  },
};
